import React from 'react';
import { runInAction, toJS } from 'mobx';

import { TableNode, TableRow, tableStore } from '../../../stores/table-store';
import {
  buildPanelCellDraftUpdate,
  buildPanelCellCommitUpdate,
} from '../expanded-cell-view/panel-cell-updates';

/**
 * The focus / change / blur protocol for a free-text field in the node panel.
 *
 * This is the Expanded Cell View's commit path (PSTUDIO-3729 + 3882 + 2154),
 * re-expressed for an arbitrary `(rowId, columnId)` rather than for whatever
 * cell happens to be selected. The three rules it preserves are the reason it
 * is a shared hook and not re-typed per field:
 *
 * 1. **On change, write the RAW string.** Canonicalizing per keystroke split a
 *    trailing `|` out of `whatsNext` the instant it was typed, so the
 *    separator could never be entered (PSTUDIO-3882). Canonicalization waits
 *    for blur.
 * 2. **On blur, canonicalize — but only if it actually changed.** An untouched
 *    field must not rewrite its own value, or focusing and leaving a field
 *    would rewrite `[]` to `[0]` and land an empty undo entry (PSTUDIO-3729).
 * 3. **One undo entry per committed edit**, via `commitCellEdit` against the
 *    snapshot taken on focus (PSTUDIO-2154).
 *
 * A field that SHOWS something other than the stored text (the Analytics Events
 * textarea shows its one-line JSON laid out) passes `displayOf`, which maps the
 * stored text to what the field displays. Blur then also treats a draft equal to
 * that display as no change: the user typed and typed back what the field showed
 * when it was entered, so nothing they can see differs. The comparison with the
 * stored text stays, so focusing and leaving an untouched field is still a no-op.
 *
 * `isCellBeingEdited` is set while focused because the undo hotkey and the
 * grid's keyboard handlers key off it — a panel editor that left it false would
 * let Cmd+Z fire mid-edit. It is cleared SYNCHRONOUSLY on blur; see invariant 8
 * in the pro-studio map for what a deferred clear costs.
 */
export const useNodeFieldCommit = (
  rowId: string,
  columnId: string,
  displayOf?: (stored: string) => string
) => {
  const originalOnFocusRef = React.useRef<string>('');
  const shownOnFocusRef = React.useRef<string | null>(null);
  const beforeEditSnapshotRef = React.useRef<Partial<TableRow>>({});

  const isJsonColumn = tableStore.isJsonColumn(
    rowId,
    columnId as keyof TableNode
  );

  const onFocus = React.useCallback(() => {
    runInAction(() => {
      tableStore.isCellBeingEdited = true;
      originalOnFocusRef.current =
        tableStore.cellValueForEditor(rowId, columnId)?.toString() ?? '';
      shownOnFocusRef.current = displayOf
        ? displayOf(originalOnFocusRef.current)
        : null;
      beforeEditSnapshotRef.current = {
        [columnId]: toJS(
          tableStore.getValue(rowId, columnId as keyof TableNode)
        ),
        ...(isJsonColumn
          ? {
              [`${columnId}Map`]: toJS(
                tableStore.getValue(rowId, `${columnId}Map` as keyof TableNode)
              ),
            }
          : {}),
      } as Partial<TableRow>;
    });
  }, [rowId, columnId, isJsonColumn, displayOf]);

  const onChange = React.useCallback(
    (raw: string) => {
      runInAction(() => {
        tableStore.isCellBeingEdited = true;
        tableStore.updateRow({
          rowId,
          updates: buildPanelCellDraftUpdate(columnId, raw, isJsonColumn),
          skipValidationRules: true,
          saveImmediately: false,
        });
        tableStore.markRowDirty(rowId);
      });
    },
    [rowId, columnId, isJsonColumn]
  );

  const onBlur = React.useCallback(() => {
    const rawDraft =
      tableStore.cellValueForEditor(rowId, columnId)?.toString() ?? '';

    if (
      rawDraft !== originalOnFocusRef.current &&
      rawDraft !== shownOnFocusRef.current
    ) {
      runInAction(() => {
        tableStore.updateRow({
          rowId,
          updates: buildPanelCellCommitUpdate(columnId, rawDraft, isJsonColumn),
          skipValidationRules: true,
          saveImmediately: false,
        });
      });
      tableStore.saveUnsavedChanges();
      tableStore.commitCellEdit({
        rowId,
        columnId,
        before: beforeEditSnapshotRef.current,
      });
    } else {
      // Typed and typed back: `onChange` already marked the row dirty, and no
      // commit is coming to clear it. Without this the row stays in
      // `unsavedRowIds`, so `hasUnsavedChanges` reads true for a node nothing
      // changed on. Same else-branch the Expanded Cell View's blur has.
      tableStore.clearRowDirty(rowId);
    }

    runInAction(() => {
      tableStore.isCellBeingEdited = false;
    });
  }, [rowId, columnId, isJsonColumn]);

  return { onFocus, onChange, onBlur };
};
